import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { RpcClientContext } from "../../contexts/rpc";

// Copied from redo-web, but don't want to depend on redo-web here
interface AddressAutocompleteFields {
  address1: string;
  address2?: string;
  city: string;
  province: string;
  zip: string;
  countryCode: string;
}
export interface AddressAutocompleteActions {
  getAddressSuggestionsFromAddressLine: (
    query: string,
    sessionToken?: string,
    countryCode?: string,
  ) => Promise<{ keyToFullObject: string; displayText: string }[]>;

  getAddressFromAddressLineSuggestion: (
    suggestionId: string,
  ) => Promise<AddressAutocompleteFields | null>;

  getAddressSuggestionsFromName: () => Promise<
    { keyToFullObject: FulfillmentOrderAddress; displayText: string }[]
  >;

  getAddressFromNameSuggestion: (
    destinationAddress: FulfillmentOrderAddress,
  ) => Promise<AddressAutocompleteFields & { name: string }>;
}
// End of copied types

export const useAddressAutocompleteActions = (): AddressAutocompleteActions => {
  const customerPortalRpcClient = useRequiredContext(RpcClientContext);

  const getAddressSuggestionsFromAddressLine = useHandler(
    async (query: string, sessionToken?: string, countryCode?: string) => {
      const { suggestions } =
        await customerPortalRpcClient.getAutocompleteSuggestions({
          query,
          sessionToken,
          countryCode,
        });
      return suggestions.map((suggestion) => ({
        keyToFullObject: suggestion.keyToFullObject,
        displayText: suggestion.displayText,
      }));
    },
  );

  const getAddressFromAddressLineSuggestion = useHandler(
    async (suggestionId: string) => {
      const { address } =
        await customerPortalRpcClient.getAddressFromSuggestion({
          suggestionId,
        });
      return address as AddressAutocompleteFields | null;
    },
  );

  const getAddressSuggestionsFromName = async () => {
    // Customer portal doesn't have name-based address lookup like merchant app
    // Return empty array for now - this could be implemented later if needed
    return [];
  };

  const getAddressFromNameSuggestion = useHandler(
    async (destinationAddress: FulfillmentOrderAddress) => {
      return {
        name: destinationAddress.name ?? "",
        address1: destinationAddress.address1 ?? "",
        address2: destinationAddress.address2 ?? "",
        city: destinationAddress.city ?? "",
        province: destinationAddress.province ?? "",
        zip: destinationAddress.zip ?? "",
        countryCode: destinationAddress.countryCode as RedoShopifyCountryCode,
      };
    },
  );

  return {
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
  };
};
